"use client";

import { ArrowRight, Mail, MapPin, Phone, Upload } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const projectTypes = [{
  value: "field-trip",
  label: "Field Trip"
}, {
  value: "volunteer",
  label: "Volunteer"
}, {
  value: "supply-donation",
  label: "Supply Donation"
}, {
  value: "meeting",
  label: "Parent Meeting"
}, {
  value: "other",
  label: "Other"
}];
const materialOptions = [{
  id: "art-supplies",
  label: "Art Supplies"
}, {
  id: "books",
  label: "Books"
}, {
  id: "snacks",
  label: "Healthy Snacks"
}, {
  id: "stationery",
  label: "Stationery"
}, {
  id: "crafts",
  label: "Craft Materials"
}, {
  id: "prizes",
  label: "Classroom Prizes"
}];
const timelineOptions = [{
  value: "urgent",
  label: "This Week"
}, {
  value: "standard",
  label: "This Month"
}, {
  value: "flexible",
  label: "Next Term"
}, {
  value: "exploratory",
  label: "General Inquiry"
}];
function InquiryFormBody() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [budgetValue, setBudgetValue] = useState<number>(25000);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const toggleMaterial = (id: string) => {
    setSelectedMaterials(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).slice(0, 10);
      setFileNames(files.map(f => f.name));
    }
  };
  const formatBudget = (val: number) => {
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val}`;
  };
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-8 lg:p-12 rounded-lg bg-background">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">— Message Sent</p>
        <h3 className="font-default text-3xl md:text-4xl text-foreground mb-4">
          Thank you! <em className="text-primary not-italic italic">We've received your note.</em>
        </h3>
        <p className="text-muted-foreground mb-8 leading-relaxed">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest rounded-lg">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-12">
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">Inquiry Type</span>
          <div className="flex-1 h-px bg-border"></div>
        </div>
        <FormField control={control} name="projectType" rules={{
        required: "Please select an inquiry type"
      }} render={({
        field
      }) => <FormItem>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {projectTypes.map((type, i) => <div key={type.value} data-index={i} className="flex items-center gap-3 border border-border px-4 py-4 cursor-pointer hover:border-primary transition-colors rounded-lg has-[:checked]:border-primary has-[:checked]:bg-accent/30">
                      <RadioGroupItem value={type.value} id={`pt-${type.value}`} />
                      <label htmlFor={`pt-${type.value}`} className="flex-1 cursor-pointer">
                        <span className="font-default text-lg text-foreground">{type.label}</span>
                      </label>
                    </div>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="space-y-8">
        <div className="flex items-center gap-4">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">Parent Details</span>
          <div className="flex-1 h-px bg-border"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
          <FormField control={control} name="name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Min 2 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Parent Name *
                </FormLabel>
                <FormControl>
                  <Input placeholder="Jane Smith" className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg font-default h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Email Address *
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jane@example.com" className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg font-default h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Phone Number *
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+1 (555) 123-4567" className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg font-default h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Student Name <span className="opacity-50">(Optional)</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Child's name" className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg font-default h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div className="space-y-8">
        <div className="flex items-center gap-4">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">Message Details</span>
          <div className="flex-1 h-px bg-border"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
          <FormField control={control} name="subject" rules={{
          required: "Subject is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Subject *
                </FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Field trip question" className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg font-default h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="location" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Preferred Contact Time
                </FormLabel>
                <FormControl>
                  <Input placeholder="Morning/Afternoon" className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg font-default h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="dimensions" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Classroom Event
                </FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Science Fair" className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg font-default h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="timeline" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Urgency
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg font-default h-11 w-full">
                      <SelectValue placeholder="Select urgency" />
                    </SelectTrigger>
                    <SelectContent>
                      {timelineOptions.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i}>
                          {opt.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="space-y-3 pt-2">
          <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground block">
            Resource Interests
          </label>
          <div className="flex flex-wrap gap-2">
            {materialOptions.map((mat, i) => <Button key={mat.id} type="button" onClick={() => toggleMaterial(mat.id)} variant={selectedMaterials.includes(mat.id) ? "default" : "outline"} size="sm" className={`font-mono text-xs uppercase tracking-widest rounded-lg ${selectedMaterials.includes(mat.id) ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border-border text-foreground hover:border-primary"}`} data-index={i}>
                {mat.label}
              </Button>)}
          </div>
        </div>

        <FormField control={control} name="message" rules={{
        required: "Please describe your request",
        minLength: {
          value: 10,
          message: "Min 10 characters"
        }
      }} render={({
        field
      }) => <FormItem className="pt-2">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Message Details *
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Please share any questions or details about your request." rows={6} className="border-0 border-b border-border px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-foreground text-base font-default resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="pt-2">
          <label htmlFor="file-upload" className="block border border-dashed border-border hover:border-primary transition-colors p-8 cursor-pointer text-center rounded-lg">
            <Upload className="size-6 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Upload Permission Slips · Max 10 Files
            </p>
            {fileNames.length > 0 && <div className="mt-4 space-y-1">
                {fileNames.map((name, i) => <p key={i} data-index={i} className="font-mono text-xs text-primary">
                    {name}
                  </p>)}
              </div>}
            <input id="file-upload" type="file" multiple className="hidden" onChange={handleFileChange} />
          </label>
        </div>
      </div>

      {submitError && <div className="border border-destructive p-4 rounded-lg">
          <p className="text-destructive font-mono text-sm">{submitError}</p>
        </div>}

      <div className="space-y-4 pt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-14 font-mono text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-shadow rounded-lg">
          {isSubmitting ? <>Sending...</> : <>
              Submit Request
              <ArrowRight className="size-4 ml-2" />
            </>}
        </Button>
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground text-center">
          Privacy Guaranteed · Classroom Use Only
        </p>
      </div>
    </Form>;
}
export default function SayHello() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4803" id="say-hello" className="bg-background text-foreground py-20 md:py-28 lg:py-36 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-28 space-y-10">
              <p className="font-mono text-xs uppercase tracking-widest text-primary">— CONTACT</p>

              <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-foreground">
                Reach out
                <br />
                <em className="not-italic text-primary italic">to Class 2A.</em>
              </h2>

              <p className="text-muted-foreground text-lg leading-relaxed max-w-md font-default">
                Have a question about the curriculum or need to submit a form? We're here to help.
              </p>

              <div className="h-px bg-border w-16"></div>

              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <Phone className="size-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      School Office
                    </p>
                    <p className="font-mono text-sm text-foreground">+1 (555) 202-2024</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Mail className="size-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      Teacher Email
                    </p>
                    <p className="font-mono text-sm text-foreground break-all">teacher@class2a.edu</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="size-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      Location
                    </p>
                    <p className="font-mono text-sm text-foreground">Room 2A, Main Building</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <FormProvider formKey="contact_form_class2_a_hub" formId="contact_form_class2_a_hub" sectionName="say-hello" defaultValues={{
            projectType: "",
            name: "",
            email: "",
            phone: "",
            company: "",
            subject: "",
            location: "",
            dimensions: "",
            timeline: "",
            message: ""
          }} validationRules={{
            projectType: {
              required: true
            },
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            subject: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Request" successMessage="Your message has been delivered to the classroom. We'll get back to you as soon as possible." errorMessage="Something went wrong. Please try again or call the school office.">
              <InquiryFormBody />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}